import { useEffect } from "react";
import Index from "./Index";
import { WabiHero } from "./WabiInspiredLanding";

/**
 * Preview-only homepage experiment:
 * - uses the /invite hero at the top
 * - keeps the existing homepage navigation and every section below its old hero
 * - restyles the existing navigation as a floating Liquid Glass-inspired capsule
 *
 * Production behavior remains untouched until this branch is intentionally merged.
 */
export default function PreviewHomepage() {
  useEffect(() => {
    const root = document.querySelector(".invite-hero-homepage-preview");
    if (!root) return;

    const headline = root.querySelector(".wabi-copy h1");
    const subhead = root.querySelector(".wabi-copy p");
    const cta = root.querySelector(".wabi-copy-cta");

    if (headline) {
      headline.innerHTML = "<span>Your brand,</span><span>built to grow.</span>";
    }

    if (subhead) {
      subhead.textContent =
        "Turn profile visits into bookings with a branded flow built to convert, recover lost clients, and bring them back.";
    }

    if (cta) {
      cta.textContent = "Build my free page →";
    }
  }, []);

  return (
    <div className="invite-hero-homepage-preview">
      <style>{`
        .invite-hero-homepage-preview .hero-takeover {
          display: none;
        }

        .invite-hero-homepage-preview .wabi-landing.is-embedded .wabi-hero {
          min-height: 980px !important;
          padding-top: 142px !important;
        }

        .invite-hero-homepage-preview .wabi-copy {
          width: min(760px, calc(100% - 32px)) !important;
        }

        .invite-hero-homepage-preview .wabi-copy h1 {
          font-family: "Bricolage Grotesque", Inter, ui-sans-serif, system-ui, sans-serif !important;
          font-size: clamp(52px, 5.8vw, 82px) !important;
          line-height: .94 !important;
          letter-spacing: -.05em !important;
          font-weight: 600 !important;
        }

        .invite-hero-homepage-preview .wabi-copy h1 span {
          display: block !important;
        }

        .invite-hero-homepage-preview .wabi-copy p {
          max-width: 620px !important;
          margin-top: 24px !important;
          font-size: clamp(16px, 1.2vw, 20px) !important;
          line-height: 1.42 !important;
        }

        .invite-hero-homepage-preview .wabi-copy-cta {
          margin-top: 28px !important;
        }

        .invite-hero-homepage-preview .wabi-stage {
          margin-top: 36px !important;
        }

        /* Floating functional layer: glass is reserved for navigation, not content. */
        .invite-hero-homepage-preview nav.fixed {
          top: 14px;
          left: 50%;
          right: auto;
          width: min(calc(100% - 28px), 1120px);
          transform: translateX(-50%);
          border: 0;
          border-radius: 999px;
          background:
            linear-gradient(180deg, rgba(255,255,255,.58), rgba(255,255,255,.30));
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          backdrop-filter: blur(24px) saturate(180%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.92),
            inset 0 -1px 0 rgba(255,255,255,.28),
            0 1px 0 rgba(20,20,22,.08),
            0 12px 38px rgba(20,20,22,.14);
          overflow: hidden;
          isolation: isolate;
        }

        .invite-hero-homepage-preview nav.fixed::before {
          content: "";
          position: absolute;
          inset: 1px;
          border-radius: inherit;
          pointer-events: none;
          background:
            radial-gradient(130% 90% at 18% -30%, rgba(255,255,255,.80), transparent 48%),
            linear-gradient(110deg, rgba(255,255,255,.18), transparent 42%);
          opacity: .78;
          z-index: -1;
        }

        .invite-hero-homepage-preview nav.fixed > div {
          max-width: none;
          height: 62px;
          padding-left: 18px;
          padding-right: 10px;
        }

        .invite-hero-homepage-preview nav.fixed > div > a:first-child {
          transform: translateX(3px);
        }

        .invite-hero-homepage-preview nav.fixed a,
        .invite-hero-homepage-preview nav.fixed button {
          -webkit-tap-highlight-color: transparent;
        }

        .invite-hero-homepage-preview nav.fixed .nav-link {
          color: rgba(34,34,36,.70);
          font-weight: 600;
          border-radius: 999px;
          padding: 9px 12px;
          transition: background-color .2s ease, color .2s ease, transform .2s ease;
        }

        .invite-hero-homepage-preview nav.fixed .nav-link:hover {
          color: rgba(20,20,22,.94);
          background: rgba(255,255,255,.48);
        }

        .invite-hero-homepage-preview nav.fixed a[href="/fancy-signin"] {
          border-radius: 999px;
          padding: 10px 14px;
          color: rgba(34,34,36,.72);
          font-weight: 600;
        }

        .invite-hero-homepage-preview nav.fixed a[href^="/magic-onboarding-v2"],
        .invite-hero-homepage-preview .wabi-copy-cta {
          min-height: 44px;
          border-radius: 999px;
          padding: 0 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #5956F6 !important;
          color: #FFFFFF !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.24),
            0 8px 22px rgba(89,86,246,.28);
        }

        .invite-hero-homepage-preview nav.fixed a[href^="/magic-onboarding-v2"]:hover,
        .invite-hero-homepage-preview .wabi-copy-cta:hover {
          background: #4845E8 !important;
          color: #FFFFFF !important;
          transform: translateY(-1px);
        }

        @media (max-width: 640px) {
          .invite-hero-homepage-preview nav.fixed {
            top: 10px;
            width: calc(100% - 20px);
          }

          .invite-hero-homepage-preview nav.fixed > div {
            height: 58px;
            padding-left: 15px;
            padding-right: 7px;
          }

          .invite-hero-homepage-preview nav.fixed > div > a:first-child {
            transform: translateX(2px);
          }

          .invite-hero-homepage-preview nav.fixed a[href^="/magic-onboarding-v2"] {
            min-height: 44px;
            padding: 0 16px;
          }

          .invite-hero-homepage-preview .wabi-landing.is-embedded .wabi-hero {
            min-height: 900px !important;
            padding-top: 118px !important;
          }

          .invite-hero-homepage-preview .wabi-copy h1 {
            font-size: clamp(42px, 11.5vw, 58px) !important;
            line-height: .96 !important;
          }

          .invite-hero-homepage-preview .wabi-copy p {
            max-width: 330px !important;
            margin-top: 20px !important;
            font-size: 16px !important;
            line-height: 1.4 !important;
          }

          .invite-hero-homepage-preview .wabi-copy-cta {
            margin-top: 24px !important;
          }

          .invite-hero-homepage-preview .wabi-stage {
            margin-top: 30px !important;
          }
        }

        @media (prefers-reduced-transparency: reduce), (prefers-contrast: more) {
          .invite-hero-homepage-preview nav.fixed {
            background: rgba(248,248,248,.96);
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
            box-shadow:
              inset 0 0 0 1px rgba(20,20,22,.14),
              0 8px 24px rgba(20,20,22,.12);
          }

          .invite-hero-homepage-preview nav.fixed::before {
            display: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .invite-hero-homepage-preview nav.fixed *,
          .invite-hero-homepage-preview nav.fixed *::before,
          .invite-hero-homepage-preview nav.fixed *::after {
            transition: none !important;
          }
        }
      `}</style>
      <WabiHero embedded />
      <Index />
    </div>
  );
}
